import React, { useState, useEffect } from "react";
import ReactEcharts from "echarts-for-react";
import * as echarts from "echarts";
import PropTypes from "prop-types";
import _ from "lodash";
import { makeStyles } from "@material-ui/core/styles";
import ChartStyle from "styles/ChartStyle";
import { chartLineWidth } from "styles/variables";

const useStyles = makeStyles((theme) => ({
  chartContainer: {
    marginTop: 16,
    marginLeft: 16,
    width: "100%",
    display: "block",
  },
}));

const GenericEchart = ({
  legend,
  lblSaveImage = "",
  xAisData,
  seriesData,
  chartType = "line",
  colorConfig = [],
  yAxisConfig,
}) => {
  const classes = useStyles();
  const [seriesType, setSeriesType] = useState(chartType);
  const [chartData, setChartData] = useState([]);
  const [color, setColorArray] = useState([]);
  const [stackState, setStackState] = useState("");

  useEffect(() => {
    echarts.registerTheme("GenericChartTheme", ChartStyle);
  }, []);

  useEffect(() => {
    let seriesDataArray = [];
    let colorArray = colorConfig.length ? colorConfig : [];

    _.forOwn(seriesData, function (value) {
      _.forOwn(value, function (value2, key2) {
        if (key2 !== "date") {
          let eachChartSeries = {};
          eachChartSeries.name = legend[key2] || key2;
          eachChartSeries.type = chartType;
          if (key2.toLowerCase().includes("count")) {
            eachChartSeries.yAxisIndex = 1;
          }
          eachChartSeries.data = value2;
          eachChartSeries.lineStyle = { width: chartLineWidth };
          eachChartSeries.symbolSize =
            seriesData[0]["date"].length > 60 ? 4 : 8;
          eachChartSeries.barGap = 0;
          eachChartSeries.barMaxWidth = 20;
          seriesDataArray.push(eachChartSeries);
        }
      });
    });

    setChartData(seriesDataArray);
    setColorArray(colorArray);
  }, [seriesData, seriesType, legend, colorConfig]);

  const magicTypeChanged = (param) => {
    if (param.currentType === "stack") {
      setStackState("__ec_magicType_stack__");
    } else if (param.currentType === "tiled") {
      setStackState(null);
    } else {
      setSeriesType(param.currentType);
    }
  };

  const onEvents = {
    magictypechanged: magicTypeChanged,
  };

  return (
    <div className={classes.chartContainer}>
      <ReactEcharts
        option={{
          tooltip: {
            trigger: "axis",
            axisPointer: { type: "none" },
          },
          grid: {
            left: 64,
            top: 48,
            right: 48,
            bottom: 32,
          },
          legend: {
            show: true,
            data: Object.values(legend),
          },
          toolbox: {
            show: false,
            feature: {
              saveAsImage: {
                show: true,
                title: "Save",
                name: lblSaveImage,
                icon: "image://../../static/images/font-icon/save-chart.svg",
              },
            },
          },
          xAxis: {
            type: "category",
            data: xAisData,
          },
          yAxis: yAxisConfig.map(axis => ({
            type: axis.type || "value",
            name: axis.name || "",
            alignTicks: true,
            axisLine: { show: true },
            axisLabel: { formatter: "{value}" },
          })),
          series: chartData,
          color: color,
        }}
        theme="GenericChartTheme"
        notMerge={true}
        lazyUpdate={true}
        onEvents={onEvents}
        style={{ height: "350px", width: "98%" }}
      />
    </div>
  );
};

GenericEchart.propTypes = {
  legend: PropTypes.object.isRequired,
  lblSaveImage: PropTypes.string,
  xAisData: PropTypes.array.isRequired,
  seriesData: PropTypes.array.isRequired,
  chartType: PropTypes.oneOf(["line", "bar"]),
  colorConfig: PropTypes.array,
  yAxisConfig: PropTypes.array,
};

export default GenericEchart;
